// src/components/DashboardContentHeader.jsx
import React, { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faSignOutAlt, faSpinner, faChevronDown, faBars } from "@fortawesome/free-solid-svg-icons";
import { NavigationContext } from "navigation-react";
import mainLogo from "./images/mainlogo.png";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

const DashboardContentHeader = () => {
  const { stateNavigator } = useContext(NavigationContext);
  const username = ls.get("username") || "Guest";
  const logoutToken = ls.get("userlogout_token");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      if (!logoutToken) throw new Error("No logout token found");

      const logoutUrl = `${process.env.REACT_APP_BASE_URL}/user/logout?_format=json&token=${logoutToken}`;

      const res = await fetch(logoutUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok && res.status !== 204) {
        const text = await res.text();
        throw new Error(`Logout failed (status ${res.status}): ${text}`);
      }

      console.log("✅ Logout successful");

      ls.clear();
      stateNavigator.navigate("login");
    } catch (err) {
      console.error("Logout error:", err);
      ls.clear();
      stateNavigator.navigate("login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  return (
    <header style={styles.header}>
      <div style={styles.logoSection}>
        <button style={styles.mobileMenuBtn} onClick={() => {}}>
          <FontAwesomeIcon icon={faBars} size="lg" />
        </button>
        <div style={styles.logoWrapper}>
          <img src={mainLogo} alt="Prosep Services Logo" style={styles.logoImage} />
          <div style={styles.logoTextWrapper}>
            <h1 style={styles.logoTitle}>Prosep Services</h1>
            <span style={styles.logoSubtitle}>Point of Sale System</span>
          </div>
        </div>
      </div>

      <nav style={styles.navSection}>
        <div style={styles.userMenu} onClick={toggleDropdown}>
          <div style={styles.userAvatar}>
            <FontAwesomeIcon icon={faUser} style={styles.avatarIcon} />
          </div>
          <div style={styles.userInfo}>
            <span style={styles.userName}>{username}</span>
            <FontAwesomeIcon icon={faChevronDown} style={styles.dropdownIcon} />
          </div>
        </div>

        {showDropdown && (
          <div style={styles.dropdown}>
            <div style={styles.dropdownItem}>
              <FontAwesomeIcon icon={faUser} style={styles.dropdownIconLeft} />
              <span>My Profile</span>
            </div>
            <div style={styles.dropdownDivider}></div>
            <button 
              style={styles.logoutBtn} 
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <FontAwesomeIcon 
                icon={isLoggingOut ? faSpinner : faSignOutAlt} 
                spin={isLoggingOut}
                style={styles.dropdownIconLeft} 
              />
              {isLoggingOut ? "Logging out..." : "Logout"}
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 32px",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e8ecf1",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    position: "sticky",
    top: 0,
    zIndex: 100,
    minHeight: "70px",
  },
  logoSection: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  mobileMenuBtn: {
    display: "none",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px",
    color: "#3d3d3d",
    fontSize: "20px",
  },
  logoWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  logoImage: {
    height: "48px",
    width: "auto",
  },
  logoTextWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  logoTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#1a1a2e",
    letterSpacing: "-0.5px",
    lineHeight: 1.2,
  },
  logoSubtitle: {
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "400",
    letterSpacing: "0.3px",
  },
  navSection: {
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  userMenu: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "6px 12px 6px 6px",
    borderRadius: "50px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e8ecf1",
  },
  userMenuHover: {
    backgroundColor: "#f0f0f0",
  },
  userAvatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "#1a1a2e",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
  },
  avatarIcon: {
    fontSize: "16px",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  userName: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#1a1a2e",
  },
  dropdownIcon: {
    fontSize: "12px",
    color: "#6b7280",
    transition: "transform 0.2s ease",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    border: "1px solid #e8ecf1",
    minWidth: "220px",
    padding: "8px",
    zIndex: 1000,
    animation: "dropdownFadeIn 0.2s ease",
  },
  dropdownItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background 0.2s ease",
    fontSize: "14px",
    color: "#1a1a2e",
  },
  dropdownDivider: {
    height: "1px",
    backgroundColor: "#e8ecf1",
    margin: "6px 8px",
  },
  dropdownIconLeft: {
    fontSize: "16px",
    color: "#6b7280",
    width: "20px",
  },
  logoutBtn: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "10px 16px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontSize: "14px",
    color: "#dc3545",
    transition: "all 0.2s ease",
  },
  logoutBtnHover: {
    backgroundColor: "#fff5f5",
  },
  logoutBtnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

// Add keyframes for dropdown animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes dropdownFadeIn {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 768px) {
    .dashboard-header .logoTitle {
      font-size: 16px !important;
    }
    .dashboard-header .logoSubtitle {
      font-size: 10px !important;
    }
    .dashboard-header .userName {
      font-size: 13px !important;
    }
    .dashboard-header .mobileMenuBtn {
      display: block !important;
    }
    .dashboard-header .logoImage {
      height: 36px !important;
    }
    .dashboard-header .userMenu {
      padding: 4px 8px !important;
    }
    .dashboard-header .userAvatar {
      width: 30px !important;
      height: 30px !important;
    }
  }

  @media (max-width: 480px) {
    .dashboard-header .logoTitle {
      font-size: 14px !important;
    }
    .dashboard-header .logoImage {
      height: 30px !important;
    }
    .dashboard-header .userName {
      display: none !important;
    }
    .dashboard-header .userMenu {
      padding: 4px !important;
    }
    .dashboard-header .dropdown {
      min-width: 180px !important;
      right: -10px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

// Add class to container for CSS targeting
// The styles are applied via the className on the main div

export default DashboardContentHeader;